import { useEffect, useRef, useState } from 'react';

declare global {
  interface Window {
    turnstile?: {
      render: (
        container: HTMLElement,
        options: {
          sitekey: string;
          theme?: 'light' | 'dark' | 'auto';
          callback?: (token: string) => void;
          'expired-callback'?: () => void;
          'error-callback'?: () => void;
        }
      ) => string;
      reset: (widgetId?: string) => void;
      remove: (widgetId?: string) => void;
    };
    onCadecTurnstileLoad?: () => void;
  }
}

const SCRIPT_ID = 'cadec-turnstile-script';
const SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY?.trim() ?? '';

let scriptPromise: Promise<void> | null = null;

function loadTurnstileScript(): Promise<void> {
  if (window.turnstile) {
    return Promise.resolve();
  }
  if (scriptPromise) {
    return scriptPromise;
  }
  scriptPromise = new Promise((resolve, reject) => {
    const existing = document.getElementById(SCRIPT_ID);
    if (existing) {
      window.onCadecTurnstileLoad = () => resolve();
      return;
    }
    window.onCadecTurnstileLoad = () => resolve();
    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?onload=onCadecTurnstileLoad';
    script.async = true;
    script.defer = true;
    script.onerror = () => reject(new Error('Turnstile script failed to load'));
    document.head.appendChild(script);
  });
  return scriptPromise;
}

type Props = {
  onToken: (token: string) => void;
  onExpire: () => void;
  onError?: () => void;
};

export function turnstileEnabled(): boolean {
  return SITE_KEY !== '';
}

export default function TurnstileWidget({ onToken, onExpire, onError }: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);
  const onTokenRef = useRef(onToken);
  const onExpireRef = useRef(onExpire);
  const onErrorRef = useRef(onError);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    onTokenRef.current = onToken;
    onExpireRef.current = onExpire;
    onErrorRef.current = onError;
  });

  useEffect(() => {
    if (!SITE_KEY || !containerRef.current) {
      return;
    }

    let cancelled = false;

    loadTurnstileScript()
      .then(() => {
        if (cancelled || !containerRef.current || !window.turnstile) {
          return;
        }
        if (widgetIdRef.current) {
          window.turnstile.remove(widgetIdRef.current);
        }
        widgetIdRef.current = window.turnstile.render(containerRef.current, {
          sitekey: SITE_KEY,
          theme: 'light',
          callback: (token) => onTokenRef.current(token),
          'expired-callback': () => onExpireRef.current(),
          'error-callback': () => {
            onExpireRef.current();
            onErrorRef.current?.();
          },
        });
      })
      .catch(() => {
        if (!cancelled) {
          setLoadError(true);
          onErrorRef.current?.();
        }
      });

    return () => {
      cancelled = true;
      if (widgetIdRef.current && window.turnstile) {
        window.turnstile.remove(widgetIdRef.current);
        widgetIdRef.current = null;
      }
    };
  }, []);

  if (!SITE_KEY) {
    return null;
  }

  if (loadError) {
    return (
      <p className="text-sm text-red-600" style={{ fontFamily: '"Noto Sans JP", sans-serif' }}>
        認証ウィジェットの読み込みに失敗しました。ページを再読み込みしてください。
      </p>
    );
  }

  return <div ref={containerRef} className="min-h-[65px]" />;
}

export function resetTurnstileWidget(widgetId?: string | null): void {
  window.turnstile?.reset(widgetId ?? undefined);
}
